'use client';

/**
 * FullSearchModal Component
 *
 * A full-featured search modal with:
 * - Source selection (toggle data sources)
 * - AI options (reranking, analysis)
 * - Search filters (date range, content type)
 * - Expanded result display
 * - Keyboard shortcuts
 *
 * Triggered by pressing Enter in InlineSearch
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useId,
} from 'react';
import { cn } from '@/lib/utils';
import {
  Search,
  Loader2,
  X,
  Command,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  SlidersHorizontal,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Check,
  Filter,
  AlertCircle,
  type LucideIcon,
} from 'lucide-react';
import { useSearchAiz } from '../hooks/useSearchAiz';
import { useSearchAizSettings } from '../hooks/useSearchAizSettings';
import { useDataSources } from '../providers';
import { getTileComponent } from '../tiles';
import { ActionMenu } from '../tiles/BaseTile';
import { getDataSource } from '../utils/dataSourceRegistry';
import { navigateToSafeUrl } from '../utils/safeNavigation';
import type { SearchResult, TileAction, DataSourceDefinition } from '../types';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// =============================================================================
// TYPES
// =============================================================================

export interface FullSearchModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Initial search query */
  initialQuery?: string;
  /** Callback when a result is selected */
  onSelect?: (result: SearchResult) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether selecting a result should close the modal */
  closeOnSelect?: boolean;
  /** Additional CSS classes */
  className?: string;
}

function hasResultActions(result: SearchResult): boolean {
  return result.type !== 'call' && result.type !== 'entity' && result.type !== 'web';
}

// =============================================================================
// KEYBOARD SHORTCUT HINTS
// =============================================================================

function KeyboardHint({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 text-xs font-mono bg-raised border border-control-edge rounded text-ink-muted">
      {children}
    </kbd>
  );
}

function ShortcutHints() {
  return (
    <div className="flex items-center gap-4 text-xs text-ink-muted">
      <span className="flex items-center gap-1">
        <KeyboardHint><ArrowUp className="w-3 h-3" /></KeyboardHint>
        <KeyboardHint><ArrowDown className="w-3 h-3" /></KeyboardHint>
        <span>Navigate</span>
      </span>
      <span className="flex items-center gap-1">
        <KeyboardHint><CornerDownLeft className="w-3 h-3" /></KeyboardHint>
        <span>Open</span>
      </span>
      <span className="flex items-center gap-1">
        <KeyboardHint>Tab</KeyboardHint>
        <span>Toggle filters</span>
      </span>
      <span className="flex items-center gap-1">
        <KeyboardHint>Esc</KeyboardHint>
        <span>Close</span>
      </span>
    </div>
  );
}

// =============================================================================
// LOADING STATE
// =============================================================================

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="relative">
        {/* Outer glow ring */}
        <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-xl animate-pulse" />
        {/* Main spinner */}
        <Loader2 className="w-16 h-16 text-cyan-400 animate-spin" />
      </div>
      <p className="text-sm text-ink-muted animate-pulse">Searching across all sources...</p>
    </div>
  );
}

// =============================================================================
// SOURCE TOGGLE
// =============================================================================

interface SourceToggleProps {
  source: DataSourceDefinition;
  isActive: boolean;
  onToggle: () => void;
}

function SourceToggle({ source, isActive, onToggle }: SourceToggleProps) {
  const IconComponent = source.icon;

  return (
    <button aria-pressed={isActive}
      onClick={onToggle}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200',
        isActive
          ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400'
          : 'bg-raised border-edge text-ink-muted hover:border-edge-strong hover:text-ink'
      )}
    >
      <IconComponent className={cn('w-4 h-4', isActive ? source.iconColor : '')} />
      <span className="text-sm font-medium">{source.name}</span>
      {isActive && <Check className="w-3 h-3 ml-1" />}
    </button>
  );
}

// =============================================================================
// FILTERS PANEL
// =============================================================================

interface FiltersPanelProps {
  activeSources: string[];
  availableSources: DataSourceDefinition[];
  onToggleSource: (sourceId: string) => void;
  aiReranking: boolean;
  onToggleReranking: (enabled: boolean) => void;
  aiAnalysis: boolean;
  onToggleAnalysis: (enabled: boolean) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

function FiltersPanel({
  activeSources,
  availableSources,
  onToggleSource,
  aiReranking,
  onToggleReranking,
  aiAnalysis,
  onToggleAnalysis,
  isExpanded,
  onToggleExpanded,
}: FiltersPanelProps) {
  return (
    <div className="border-b border-edge bg-sunken">
      {/* Collapse/Expand Header */}
      <button
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between px-4 py-2 text-ink-muted hover:text-ink transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <SlidersHorizontal className="w-4 h-4" />
          Search Options
          {activeSources.length > 0 && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-cyan-500/20 text-cyan-400">
              {activeSources.length} sources
            </span>
          )}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Data Sources */}
          <div className="p-3 rounded-lg bg-sunken border border-edge">
            <span id="fs-data-sources" className="block text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
              Data Sources
            </span>
            <div role="group" aria-labelledby="fs-data-sources" className="flex flex-wrap gap-2">
              {availableSources.map((source) => (
                <SourceToggle
                  key={source.id}
                  source={source}
                  isActive={activeSources.includes(source.id)}
                  onToggle={() => onToggleSource(source.id)}
                />
              ))}
            </div>
          </div>

          {/* AI Options */}
          <div className="p-3 rounded-lg bg-sunken border border-edge">
            <span id="fs-ai-features" className="block text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
              AI Features
            </span>
            <div role="group" aria-labelledby="fs-ai-features" className="flex gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={aiReranking}
                  aria-label="AI Reranking"
                  className={cn(
                    'relative w-9 h-5 rounded-full transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400',
                    aiReranking ? 'bg-cyan-500' : 'bg-hover'
                  )}
                  onClick={() => onToggleReranking(!aiReranking)}
                >
                  <div
                    className={cn(
                      'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform',
                      aiReranking ? 'translate-x-4' : 'translate-x-0.5'
                    )}
                  />
                </button>
                <span className="text-sm text-ink-secondary">AI Reranking</span>
                <Sparkles className={cn('w-3.5 h-3.5', aiReranking ? 'text-cyan-400' : 'text-ink-muted')} />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={aiAnalysis}
                  aria-label="Anderra Analysis"
                  className={cn(
                    'relative w-9 h-5 rounded-full transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-400',
                    aiAnalysis ? 'bg-purple-500' : 'bg-hover'
                  )}
                  onClick={() => onToggleAnalysis(!aiAnalysis)}
                >
                  <div
                    className={cn(
                      'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform',
                      aiAnalysis ? 'translate-x-4' : 'translate-x-0.5'
                    )}
                  />
                </button>
                <span className="text-sm text-ink-secondary">Anderra Analysis</span>
                <Sparkles className={cn('w-3.5 h-3.5', aiAnalysis ? 'text-purple-400' : 'text-ink-muted')} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function FullSearchModal({
  isOpen,
  onClose,
  initialQuery = '',
  onSelect,
  placeholder = 'Search everything...',
  closeOnSelect = true,
  className,
}: FullSearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const gridId = useId();

  // Local state
  const [showFilters, setShowFilters] = useState(false);
  const [activeColumn, setActiveColumn] = useState<'result' | 'actions'>('result');
  const [localQuery, setLocalQuery] = useState(initialQuery);

  // Get settings with safe fallbacks
  const { settings, isLoading: settingsLoading } = useSearchAizSettings();
  const { accessibleSources } = useDataSources();
  const accessibleSourceIds = useMemo(
    () => accessibleSources.map((source) => source.id),
    [accessibleSources]
  );
  const fallbackSourceId = accessibleSourceIds[0] ?? 'knowledge_base';
  const defaultSources = useMemo(() => {
    const configuredSources = settings?.sources?.default ?? ['knowledge_base'];
    const filteredSources = configuredSources.filter((sourceId) =>
      accessibleSourceIds.includes(sourceId)
    );
    return filteredSources.length > 0 ? filteredSources : [fallbackSourceId];
  }, [accessibleSourceIds, fallbackSourceId, settings?.sources?.default]);

  // Active sources (local state to allow modification)
  const [activeSources, setActiveSources] = useState<string[]>(() => defaultSources);

  // AI options (local state)
  const [aiReranking, setAiReranking] = useState(() =>
    settings?.ai?.rerank?.enabled ?? false
  );
  const [aiAnalysis, setAiAnalysis] = useState(() =>
    settings?.ai?.analysis?.enabled ?? false
  );

  // Results per page (larger for full modal)
  const resultsPerPage = 20;
  const showScores = settings?.results?.showScores ?? true;
  const showSources = settings?.results?.showSources ?? true;

  // Build rerank config
  const rerankConfig = useMemo(() => {
    if (!aiReranking) return undefined;
    return settings?.ai?.rerank ?? { enabled: true, tier: 'standard', scoreThreshold: 0.3 };
  }, [aiReranking, settings?.ai?.rerank]);

  // Search state
  const {
    query,
    isSearching,
    results: rawResults,
    error,
    notices,
    selectedIndex,
    search,
    clear,
    navigateSelection,
  } = useSearchAiz({
    sources: activeSources.length > 0 ? activeSources : [fallbackSourceId],
    pattern: 'unified',
    limit: resultsPerPage,
    rerank: rerankConfig,
    debounceMs: 300,
    onResultSelect: onSelect,
  });

  // Always ensure results is a safe array
  const results = Array.isArray(rawResults) ? rawResults : [];
  const activeResult = !isSearching && selectedIndex >= 0 ? results[selectedIndex] : undefined;
  const cellId = useCallback((resultId: string, column: 'result' | 'actions') =>
    `${gridId}-${column}-${encodeURIComponent(resultId)}`, [gridId]);

  useEffect(() => {
    if (!settingsLoading) {
      setActiveSources(defaultSources);
    }
    if (!settingsLoading && settings?.ai?.rerank) {
      setAiReranking(settings.ai.rerank.enabled ?? false);
    }
    if (!settingsLoading && settings?.ai?.analysis) {
      setAiAnalysis(settings.ai.analysis.enabled ?? false);
    }
  }, [defaultSources, settings, settingsLoading]);

  // Sync initial query
  useEffect(() => {
    if (isOpen && initialQuery) {
      setLocalQuery(initialQuery);
      search(initialQuery);
    }
  }, [isOpen, initialQuery, search]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  // Clear on close
  useEffect(() => {
    if (!isOpen) {
      // Don't clear immediately to prevent flash
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const items = listRef.current.querySelectorAll('[role="row"]');
      const selectedEl = items[selectedIndex] as HTMLElement;
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  /**
   * Toggle a data source
   */
  const handleToggleSource = useCallback((sourceId: string) => {
    setActiveSources(prev => {
      if (!accessibleSourceIds.includes(sourceId)) {
        return prev;
      }
      if (prev.includes(sourceId)) {
        // Don't allow deselecting all sources
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== sourceId);
      }
      return [...prev, sourceId];
    });
  }, [accessibleSourceIds]);

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalQuery(value);
      search(value);
    },
    [search]
  );

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          navigateSelection('down');
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateSelection('up');
          break;
        case 'ArrowRight':
          if (activeResult && hasResultActions(activeResult)) {
            e.preventDefault();
            setActiveColumn('actions');
          }
          break;
        case 'ArrowLeft':
          if (activeResult) {
            e.preventDefault();
            setActiveColumn('result');
          }
          break;
        case 'Enter':
          if (selectedIndex >= 0 && results[selectedIndex]) {
            e.preventDefault();
            const result = results[selectedIndex];
            if (activeColumn === 'actions' && hasResultActions(result)) {
              const actionButton = document.getElementById(cellId(result.id, 'actions'))?.querySelector('button');
              actionButton?.focus();
              actionButton?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }));
              break;
            }
            onSelect?.(result);
            navigateToSafeUrl(result.url);
            if (closeOnSelect) {
              onClose();
            }
          }
          break;
        case 'Tab':
          // Tab opens the filters; with them open it moves on into them (the switches must be reachable by keyboard).
          if (!showFilters && !e.shiftKey) {
            e.preventDefault();
            setShowFilters(true);
          }
          break;
      }
    },
    [navigateSelection, selectedIndex, results, activeResult, activeColumn, cellId, onSelect, onClose, closeOnSelect, showFilters]
  );

  /**
   * Handle result click
   */
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      onSelect?.(result);
      navigateToSafeUrl(result.url);
      if (closeOnSelect) {
        onClose();
      }
    },
    [onSelect, onClose, closeOnSelect]
  );

  /**
   * Handle tile action
   */
  const handleTileAction = useCallback(
    (action: TileAction) => {
      if (action.type === 'view' && action.url) {
        if (navigateToSafeUrl(action.url) && closeOnSelect) {
          onClose();
        }
      } else if (action.type === 'copyLink' && action.url) {
        navigator.clipboard.writeText(action.url);
      }
    },
    [onClose, closeOnSelect]
  );

  /**
   * Clear search
   */
  const handleClear = useCallback(() => {
    setLocalQuery('');
    clear();
    inputRef.current?.focus();
  }, [clear]);

  // results is already guaranteed to be a safe array from line 338

  // Escape, the backdrop press, focus containment and focus return belong to the shared dialog; it reports a dismissal here.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        layout="sections"
        size="lg"
        hideClose
        aria-label="Search"
        aria-modal="true"
        aria-describedby={undefined}
        className={cn('self-start mt-[4vh]', className)}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        {/* Search Header */}
        <DialogHeader className="border-b border-edge bg-sunken px-5 py-4">
          <DialogTitle className="sr-only">Search</DialogTitle>
          {/* Search Input Box */}
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-control-edge bg-raised">
            {isSearching ? (
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            ) : (
              <Search className="w-6 h-6 text-cyan-400" />
            )}

            <input aria-label="Search"
              role="combobox"
              aria-haspopup="grid"
              aria-expanded={true}
              aria-controls={gridId}
              aria-activedescendant={activeResult ? cellId(
                activeResult.id,
                activeColumn === 'actions' && hasResultActions(activeResult) ? 'actions' : 'result'
              ) : undefined}
              ref={inputRef}
              type="text"
              value={localQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={cn(
                'flex-1 bg-transparent text-ink placeholder:text-ink-muted',
                'text-xl font-medium outline-none'
              )}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />

            {localQuery && (
              <button aria-label="Clear search"
                onClick={handleClear}
                className="p-2 rounded-lg hover:bg-hover text-ink-muted hover:text-ink transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            <button aria-label="Toggle search filters" aria-pressed={showFilters}
              onClick={() => setShowFilters(prev => !prev)}
              className={cn(
                'p-2 rounded-lg transition-colors',
                showFilters
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'hover:bg-hover text-ink-muted hover:text-ink'
              )}
            >
              <SlidersHorizontal className="w-5 h-5" />
            </button>

            <div className="hidden sm:flex items-center gap-1 text-ink-muted border-l border-edge-strong pl-3 ml-1">
              <Command className="w-4 h-4" />
              <span className="text-xs">K</span>
            </div>
          </div>
        </DialogHeader>

        {/* Filters Panel */}
        <FiltersPanel
          activeSources={activeSources}
          availableSources={accessibleSources}
          onToggleSource={handleToggleSource}
          aiReranking={aiReranking}
          onToggleReranking={setAiReranking}
          aiAnalysis={aiAnalysis}
          onToggleAnalysis={setAiAnalysis}
          isExpanded={showFilters}
          onToggleExpanded={() => setShowFilters(prev => !prev)}
        />

        {/* Results Area */}
        <DialogBody className="min-h-[min(25rem,40dvh)] max-h-[70vh] p-0">
          {/* Loading State */}
          {isSearching && <LoadingState />}

          {!error && !isSearching && notices.length > 0 && (
            <div data-testid="search-aiz-notices" role="status" className="px-4 py-2 space-y-1 text-xs text-warning">
              {notices.map((notice) => <p key={notice}>{notice}</p>)}
              {results.length === 0 && <p>Nothing was found in the sources that could be searched.</p>}
            </div>
          )}

          {/* Error State */}
          {!isSearching && error && (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="p-4 rounded-full bg-danger-fill mb-4">
                <AlertCircle className="w-12 h-12 text-danger" />
              </div>
              <p className="text-lg font-medium text-danger mb-2">Search failed</p>
              <p className="text-sm text-ink-muted max-w-md">{error.message}</p>
            </div>
          )}

          {/* No Results */}
          {!isSearching && !error && notices.length === 0 && results.length === 0 && localQuery.length >= 2 && (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-3 min-w-0" data-watermark-id="wm-search-no-results">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded border border-edge bg-raised flex items-center justify-center">
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 text-ink-muted" />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-ink-muted">No results found</p>
                  <p className="text-xs text-ink-subtle">
                    Try different keywords or adjust your search filters
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Initial State / Type to Search */}
          {!isSearching && !error && notices.length === 0 && results.length === 0 && localQuery.length < 2 && (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-3 min-w-0" data-watermark-id="wm-search-initial">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded border border-edge bg-raised flex items-center justify-center">
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 text-ink-muted" />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-ink-muted">
                    {localQuery.length === 0 ? 'Start typing to search' : 'Type at least 2 characters'}
                  </p>
                  <p className="text-xs text-ink-subtle">
                    Search across {activeSources.length} data source{activeSources.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Results List */}
          {(isSearching || results.length === 0) && <div id={gridId} role="grid" />}
          {!isSearching && results.length > 0 && (
            <div ref={listRef} id={gridId} role="grid" className="p-3 space-y-2">
              {results.map((result, index) => {
                const TileComponent = getTileComponent(result.type);
                const hasActions = hasResultActions(result);

                return (
                  <div key={result.id} role="row" data-result-item className="relative group">
                    <TileComponent
                      result={result}
                      mode="expanded"
                      showScore={showScores}
                      showSource={showSources}
                      highlighted={index === selectedIndex}
                      gridCell={{ id: cellId(result.id, 'result'), selected: index === selectedIndex && activeColumn === 'result' }}
                      className={hasActions ? 'pr-12' : undefined}
                      onClick={() => handleResultClick(result)}
                      onAction={handleTileAction}
                    />
                    <div
                      id={cellId(result.id, 'actions')}
                      role="gridcell"
                      aria-selected={index === selectedIndex && activeColumn === 'actions' && hasActions}
                      className="absolute right-4 top-4"
                    >
                      {hasActions && <ActionMenu result={result} onAction={handleTileAction} />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogBody>

        {/* Footer */}
        <DialogFooter className="items-center justify-between border-t border-edge bg-raised px-4 py-3 sm:flex-row sm:justify-between">
          <ShortcutHints />

          <div className="flex items-center gap-4 text-sm text-ink-muted">
            {results.length > 0 && (
              <span>
                {results.length} result{results.length !== 1 ? 's' : ''}
              </span>
            )}
            {aiReranking && (
              <span className="flex items-center gap-1 text-cyan-400">
                <Sparkles className="w-3.5 h-3.5" />
                AI Enhanced
              </span>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// HOOK FOR FULL SEARCH MODAL
// =============================================================================

export interface UseFullSearchModalOptions {
  /** Initial query to populate */
  initialQuery?: string;
}

/**
 * Hook to manage full search modal state
 */
export function useFullSearchModal(options: UseFullSearchModalOptions = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(options.initialQuery ?? '');

  const open = useCallback((initialQuery?: string) => {
    if (initialQuery !== undefined) {
      setQuery(initialQuery);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return {
    isOpen,
    query,
    open,
    close,
    toggle,
  };
}

export default FullSearchModal;
